import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ExternalLink, LogIn, Store } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "../vendor/Overview";
import DataTable from "@/components/app/DataTable";
import { btn, pageSub, pageTitle } from "@/components/app/ui";

export default function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/admin/vendors").then(({ data }) => setVendors(data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async (v) => {
    const status = v.status === "active" ? "suspended" : "active";
    if (status === "suspended" && !window.confirm(`Suspend “${v.name}”? Their storefront will stop accepting orders.`)) return;
    setBusyId(v.id);
    try {
      await api.put(`/admin/vendors/${v.id}/status`, { status });
      toast.success(`“${v.name}” ${status === "active" ? "activated" : "suspended"} successfully.`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusyId(null); }
  }, [load]);

  const impersonate = useCallback(async (v) => {
    setBusyId(v.id);
    try {
      const { data } = await api.post(`/admin/vendors/${v.id}/impersonate`);
      login(data.token, data.user);
      toast.success(`Now viewing as ${v.name}.`);
      navigate("/vendor");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); setBusyId(null); }
  }, [login, navigate]);

  const columns = useMemo(() => [
    { key: "name", header: "Store", cell: (v) => (
      <div className="flex items-center gap-2.5 min-w-[180px]">
        <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 text-[11px] font-bold flex items-center justify-center shrink-0">{v.name?.[0]?.toUpperCase()}</span>
        <div className="min-w-0">
          <div className="font-semibold text-neutral-900 truncate">{v.name}</div>
          <a href={`/store/${v.slug}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[11px] text-blue-600 font-mono inline-flex items-center gap-1 hover:underline">/{v.slug} <ExternalLink size={10} /></a>
        </div>
      </div>
    ) },
    { key: "business_name", header: "Owner / business", hideBelow: "md", cell: (v) => <span className="text-slate-600">{v.business_name}</span> },
    { key: "business_category", header: "Category", hideBelow: "lg", accessor: (v) => v.business_category || "", cell: (v) => <span className="text-slate-600">{v.business_category || "—"}</span> },
    { key: "plan_name", header: "Plan", cell: (v) => <span className="inline-block text-[11px] px-2 py-0.5 rounded-md border bg-blue-50 text-blue-700 border-blue-200 font-medium">{v.plan_name}</span> },
    { key: "products_count", header: "Products", align: "center", hideBelow: "sm" },
    { key: "orders_count", header: "Orders", align: "center", hideBelow: "sm" },
    { key: "created_at", header: "Created", hideBelow: "lg", accessor: (v) => v.created_at || "", cell: (v) => <span className="text-slate-600 whitespace-nowrap">{(v.created_at || "").slice(0, 10) || "—"}</span> },
    { key: "status", header: "Status", cell: (v) => <StatusBadge status={v.status} /> },
    { key: "actions", header: "Actions", sortable: false, align: "right", cell: (v) => (
      <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => impersonate(v)} disabled={busyId === v.id} data-testid={`admin-vendor-impersonate-${v.id}`}
          className={`${btn} !h-8 !px-2.5 !text-[12px] border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}><LogIn size={13} /> Login as</button>
        <button onClick={() => toggle(v)} disabled={busyId === v.id} data-testid={`admin-vendor-toggle-${v.id}`}
          className={`${btn} !h-8 !px-2.5 !text-[12px] border ${v.status === "active" ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100" : "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"}`}>
          {v.status === "active" ? "Suspend" : "Activate"}
        </button>
      </div>
    ) },
  ], [busyId, impersonate, toggle]);

  const filters = useMemo(() => [
    { key: "status", label: "All statuses", accessor: (v) => v.status },
    { key: "plan", label: "All plans", accessor: (v) => v.plan_name },
    { key: "category", label: "All categories", accessor: (v) => v.business_category || "" },
  ], []);

  return (
    <div className="space-y-4" data-testid="admin-vendors">
      <div>
        <h1 className={pageTitle}>Vendors & Stores</h1>
        <p className={pageSub}>{vendors.length} stores on the platform.</p>
      </div>
      <DataTable testId="super-admin-vendor-table" searchTestId="admin-vendor-search" exportName="vendors" columns={columns} data={vendors} loading={loading}
        rowTestId={(v) => `admin-vendor-row-${v.id}`} searchKeys={["name", "business_name", "slug", "business_category"]} searchPlaceholder="Search store, owner, slug…"
        filters={filters} dateField={{ label: "Created", accessor: (v) => v.created_at }} defaultSort={{ key: "created_at", dir: "desc" }}
        emptyIcon={Store} emptyTitle="No stores yet" emptyText="Stores appear here when vendors sign up." />
    </div>
  );
}
